import { Grid, Stack } from '@mui/material';
import { useMemo } from 'react';

import { Card, CardContent, Divider, StatusChip, Typography } from '@occc-app/design-system';
import { separateEveryThousandByComma } from '@occc-app/lib/numbers';
import { TrendBadge } from '@occc/ui-design-system';
import { formatMoneyAmount } from '@occc-app/lib/currency';

import {
  fileStatusToColorMap,
  fileStatusToLabelMap,
} from '../scan-data-weekly-file/scan-data-weekly-file.constants';
import { PastSubmissionViewData } from '../scan-data-weekly-file/scan-data-weekly-file.types';

type OverviewProps = {
  pageTitle: string;
  pageSubtitle: string;
  submissionData: PastSubmissionViewData;
};

export const Overview = ({ submissionData, pageTitle, pageSubtitle }: OverviewProps) => {
  const trendColor = useMemo(() => {
    return submissionData.transactionsReportedChangePercent > 0
      ? 'green'
      : submissionData.transactionsReportedChangePercent < 0
      ? 'red'
      : 'gray';
  }, [submissionData.transactionsReportedChangePercent]);

  const trendArrow = useMemo(() => {
    return submissionData.transactionsReportedChangePercent > 0
      ? 'up'
      : submissionData.transactionsReportedChangePercent < 0
      ? 'down'
      : undefined;
  }, [submissionData.transactionsReportedChangePercent]);

  return (
    <Card sx={{ mb: 1, mt: 6 }}>
      <CardContent>
        <Typography variant='subtitle1' mb={3}>
          {`${pageTitle} - ${pageSubtitle}`}
        </Typography>

        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant='body2' color='textSecondary' gutterBottom>
              Transactions Reported
            </Typography>
            <Stack direction='row' alignItems='center' gap={1}>
              <Typography variant='h5'>
                {separateEveryThousandByComma(submissionData.transactionsReported)}
              </Typography>
              <TrendBadge
                color={trendColor}
                arrow={trendArrow}
                label={`${Math.abs(submissionData.transactionsReportedChangePercent)}%`}
              />
            </Stack>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Typography variant='body2' color='textSecondary' gutterBottom>
              Submission Groups
            </Typography>
            <Typography variant='h5' component='div'>
              {submissionData.submissionGroups.submitted}
              <Typography variant='body2' component='span'>
                /{submissionData.submissionGroups.total} submitted
              </Typography>
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Typography variant='body2' color='textSecondary' gutterBottom>
              File Status
            </Typography>
            <StatusChip
              label={fileStatusToLabelMap[submissionData.fileStatus]}
              variant={fileStatusToColorMap[submissionData.fileStatus]}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Typography variant='body2' color='textSecondary' gutterBottom>
              Estimated Reimbursement
            </Typography>
            <Typography variant='h5'>
              {formatMoneyAmount(submissionData.estimatedReimbursement, { showCents: false })}
            </Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};
